/**
 * Layout.tsx
 *
 * FIX-NAV: i due <Navigate> condizionali (recordingLock e !user/!accessToken)
 * erano nel corpo del render e causavano il warning React Router:
 * "You should call navigate() in a React.useEffect(), not when your component
 * is first rendered."
 *
 * Soluzione:
 * - recordingLock redirect → spostato in useEffect con navigate()
 * - !user || !accessToken redirect → spostato in useEffect con navigate()
 *
 * Mentre i redirect sono in pendenza viene mostrato il loader, evitando
 * qualsiasi render parziale della UI.
 *
 * FIX-LAYOUT: aggiunto md:ml-64 al <main> per compensare la sidebar fixed
 * da 256px (w-64) su desktop, evitando che il contenuto venga coperto.
 * Su mobile (sotto md) la Navbar è una bottom-bar fixed: nessun margin-left.
 * Il md:ml-64 è ESSENZIALE: senza, il contenuto scorre SOTTO la sidebar
 * rendendo l'app inutilizzabile su desktop (>768px).
 *
 * FIX-TS: updateAccess accetta 1-2 argomenti (token, user).
 * Rimosso il 3° argomento refreshToken che causava l'errore TypeScript
 * "Expected 1-2 arguments, but got 3".
 *
 * FIX-IMPERSONATE-LAYOUT: durante la modalità impersonazione, il useEffect
 * al mount saltava già initSession() ma eseguiva comunque me() con il cookie
 * rc_refresh dell'admin → il server restituiva il profilo admin → updateAccess()
 * sovrascriveva l'utente impersonato con l'admin → accessToken veniva azzerato
 * → navigate('/login').
 * Soluzione: se impersonationAdminToken è presente, run() esce immediatamente
 * senza chiamare né initSession() né me(). La sessione impersonata è già valida.
 */
import React, {
  Suspense,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Navbar from "./Navbar";
import BrandLogo from "./BrandLogo";
import LoadingFallback from "./LoadingFallback";
import useAuthStore from "@/store/useAuthStore";
import useUiStore from "@/store/useUiStore";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { SessionExpiredError } from "@/api/core/errors";
import { me } from "@/api";
import { isPublicAppPath } from "@/utils/publicRoutes";
import { useSwipeNavigation } from "@/hooks";
// FIX LOGOUT-TELEFONATA (20 Set 2026): keepalive GLOBALE — prima era montato
// SOLO su RecordRoute: su tutte le altre pagine il token (TTL 15 min) scadeva
// senza rinnovo e il primo resume dopo una telefonata rischiava il logout.
// Qui (Layout = tutte le pagine autenticate) rinnova ogni 10 min (4 min con
// registrazione attiva) e al ritorno in primo piano (visibilitychange).
import { useSessionKeepalive } from "@/hooks/auth/useSessionKeepalive";
import FeatureTour from "@/components/monetization/FeatureTour";
import InteractiveOnboarding from "@/components/onboarding/InteractiveOnboarding";
import ErrorBoundary from "@/components/ui/ErrorBoundary";
import SkipLink from "@/components/SkipLink";
import PrivacyConsentGate from "@/components/PrivacyConsentGate";

const Layout: React.FC = () => {
  const { user, accessToken, refreshToken, expireSession, updateAccess } =
    useAuthStore();
  const [checking, setChecking] = useState(true);
  const recordingLock = useUiStore((s) => s.recordingLock);
  const setRecordingLock = useUiStore((s) => s.setRecordingLock);
  const location = useLocation();
  const navigate = useNavigate();

  // FIX LOGOUT-TELEFONATA: keepalive globale per TUTTE le pagine autenticate.
  useSessionKeepalive();

  // FIX: Reset recordingLock on route change — se l'utente naviga via
  // senza fermare la registrazione, la sidebar rimane nascosta.
  useEffect(() => {
    if (recordingLock) {
      setRecordingLock(false);
    }
  }, [location.pathname]);

  // FASE 4: Sidebar collassabile su desktop (icon-only mode)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem("rc-sidebar-collapsed") === "true";
  });

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("rc-sidebar-collapsed", String(next));
      return next;
    });
  }, []);

  // FeatureTour (monetization): mostrato dopo il tour onboarding
  // FIX BUG #34: Non mostrare se setup già completato
  const [showTour, setShowTour] = useState(false);
  const tourEligible = useMemo(() => {
    const setupDone =
      localStorage.getItem("rc-setup-done") ||
      sessionStorage.getItem("rc-setup-done");
    return (
      !localStorage.getItem("rc-tour-done") &&
      !setupDone &&
      localStorage.getItem("rc-onboarding-done")
    );
  }, []);

  // InteractiveOnboarding: tour feature al primo login
  // FIX BUG #34 (Agosto 2026): Non mostrare il tour se l'utente ha già
  // completato il setup wizard (rc-setup-done) o se il tour è stato già visto.
  const [showOnboarding, setShowOnboarding] = useState(false);
  const onboardingEligible = useMemo(() => {
    const tourDone = localStorage.getItem("rc-onboarding-tour-done");
    const setupDone =
      localStorage.getItem("rc-setup-done") ||
      sessionStorage.getItem("rc-setup-done");
    // Non mostrare il tour se già visto OPPURE se il setup è stato completato
    return !tourDone && !setupDone && !!user;
  }, [user]);

  // QW7: Preload delle 3 route piu usate dopo il mount (evita flash loading su 4G)
  useEffect(() => {
    const timer = setTimeout(() => {
      import("@/pages/routes/PlanRoute");
      import("@/pages/garage/Garage");
      import("@/pages/events/EventsList");
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // QW9 RIMOSSO (Set 2026): il badge "(N) RideConnect" nel title del browser
  // confondeva gli utenti (sembrava un artefatto) — titolo fisso "RideConnect".
  useEffect(() => {
    document.title = "RideConnect";
  }, []);
  useEffect(() => {
    // FIX BUG #30: Non mostrare InteractiveOnboarding sulla pagina /setup
    // (SetupWizardPage ha il suo flusso dedicato).
    const isSetupPage =
      location.pathname === "/setup" || location.pathname === "/first-ride";
    if (
      !checking &&
      user &&
      onboardingEligible &&
      location.pathname === "/dashboard" &&
      !isSetupPage
    ) {
      const timer = setTimeout(() => setShowOnboarding(true), 800);
      return () => clearTimeout(timer);
    }
  }, [checking, user, onboardingEligible, location.pathname]);

  useEffect(() => {
    // Il FeatureTour (monetization) viene mostrato DOPO l'onboarding interattivo
    const onboardingDone = localStorage.getItem("rc-onboarding-tour-done");
    if (
      !checking &&
      user &&
      tourEligible &&
      onboardingDone &&
      location.pathname === "/dashboard"
    ) {
      const timer = setTimeout(() => setShowTour(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [checking, user, tourEligible, location.pathname]);

  useSwipeNavigation({
    enabled: !recordingLock,
    threshold: 100,
    velocityThreshold: 0.3,
  });

  const lastCheckRef = React.useRef<number>(0);
  // FIX: refs per evitare stale closure nell'effetto con listener visibility/focus
  const accessTokenRef = React.useRef(accessToken);
  const updateAccessRef = React.useRef(updateAccess);
  const expireSessionRef = React.useRef(expireSession);
  React.useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);
  React.useEffect(() => {
    updateAccessRef.current = updateAccess;
  }, [updateAccess]);
  React.useEffect(() => {
    expireSessionRef.current = expireSession;
  }, [expireSession]);

  // FIX: redirect unificati — evita race condition tra recordingLock e auth
  useEffect(() => {
    if (!checking && (!user || !accessToken)) {
      // Pagine pubbliche dell'app (es. /events): nessun redirect al login.
      if (isPublicAppPath(location.pathname)) return;
      navigate("/login", { replace: true });
      return;
    }
    if (recordingLock && location.pathname !== "/routes/record") {
      navigate("/routes/record", { replace: true });
    }
  }, [checking, user, accessToken, recordingLock, location.pathname, navigate]);

  useEffect(() => {
    const run = async (reason: "mount" | "resume") => {
      // FIX-IMPERSONATE-LAYOUT: se siamo in modalità impersonazione, la sessione
      // è già valida e il token impersonato è già in store.
      if (useAuthStore.getState().impersonationAdminToken) {
        if (reason === "mount") setChecking(false);
        return;
      }

      const tok = accessTokenRef.current;
      if (!tok && localStorage.getItem("rideconnect_user")) {
        try {
          await useAuthStore.getState().initSession();
        } catch {
          setChecking(false);
          return;
        }
      }

      const currentState = useAuthStore.getState();
      if (!currentState.user || !currentState.accessToken) {
        setChecking(false);
        return;
      }

      // FIX BUG #33 (Agosto 2026): setChecking(false) SUBITO dopo aver
      // verificato che abbiamo user+token. Il me() call è non-bloccante:
      // se l'API è in cold start (Render free tier), l'utente vede subito
      // la dashboard invece di "Connessione a RideConnect…" infinito.
      setChecking(false);

      try {
        const data = await me();
        if (data?.user) {
          const st = useAuthStore.getState();
          updateAccessRef.current(
            st.accessToken || currentState.accessToken,
            data.user,
          );
        }
      } catch (e: any) {
        if (e instanceof SessionExpiredError) {
          // FEAT NO-LOGOUT-DURING-RECORDING: scadenza sessione →
          // expireSession (che NON fa logout se una registrazione è attiva).
          expireSessionRef.current();
        }
      }
    };

    run("mount");

    const onResume = () => {
      const now = Date.now();
      if (now - lastCheckRef.current < 20_000) return;
      lastCheckRef.current = now;
      run("resume");
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") onResume();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onResume);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onResume);
    };
  }, []); // deps vuote intenzionali: listener permanenti, usano refs per valori freschi

  // FIX-NAV: !user/!accessToken redirect in useEffect
  // [fix-persist] Se user esiste ma accessToken è null, prova un retry
  // silenzioso di initSession prima di redirect a /login.
  // Questo copre il caso in cui il token salvato è stato rimosso ma
  // l'utente è ancora in localStorage (es. refresh scaduto durante
  // distruzione WebView).
  useEffect(() => {
    if (checking) return;

    if (!user) {
      // PAGINE PUBBLICHE (Ott 2026): /events deve restare leggibile senza
      // sessione (SEO/Search Console). Nessun redirect a /login: si mostra
      // soltanto il contenuto, senza navbar/sidebar.
      if (isPublicAppPath(location.pathname)) return;
      navigate("/login", { replace: true });
      return;
    }

    if (!accessToken) {
      // Prova un refresh silenzioso prima di arrendersi
      const store = useAuthStore.getState();
      store.initSession().finally(() => {
        const s = useAuthStore.getState();
        if (!s.accessToken) {
          navigate("/login", { replace: true });
        }
      });
    }
  }, [checking, user, accessToken, navigate]);

  /**
   * ANTI-SCHERMO-NERO (31 Ott 2026): rete di sicurezza sul caso "non
   * autenticato". Se dopo 6 secondi l'utente non è ancora idratato (refresh
   * bloccato, storage corrotto, token senza utente) si va al login: la
   * schermata di attesa non deve mai diventare permanente.
   */
  useEffect(() => {
    if (checking) return;
    if (user && accessToken) return;
    if (isPublicAppPath(location.pathname)) return;
    const t = setTimeout(() => {
      const s = useAuthStore.getState();
      if (!s.user || !s.accessToken) navigate("/login", { replace: true });
    }, 6_000);
    return () => clearTimeout(t);
  }, [checking, user, accessToken, navigate]);

  // Mostra loader mentre stiamo controllando la sessione
  // oppure mentre stiamo per redirigere (evita flash di UI parziale)
  if (checking) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-4 p-6">
        <BrandLogo size="lg" />
        <div className="text-neutral-400">Connessione a RideConnect…</div>
      </div>
    );
  }

  // Se non autenticato NON si resta a schermo nero.
  // ANTI-SCHERMO-NERO (31 Ott 2026): qui c'era `return null`. Se il refresh
  // silenzioso non rispondeva (rete ballerina subito dopo il login Google) o
  // il token c'era ma l'utente no, il pannello restava completamente nero,
  // senza scampo e senza nemmeno un pulsante. Ora si vede un loader con
  // uscita manuale, e il watchdog sotto riporta al login dopo 6 secondi.
  if (!user || !accessToken) {
    // ...tranne sulle pagine pubbliche dell'app (es. /events): lì si mostra
    // solo il contenuto pubblico, senza guscio applicativo.
    if (isPublicAppPath(location.pathname)) return <Outlet />;
    return (
      <div
        className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-4 p-6"
        data-testid="auth-pending-screen"
      >
        <BrandLogo size="lg" />
        <div className="text-neutral-400">Connessione a RideConnect…</div>
        <button
          type="button"
          onClick={() => navigate("/login", { replace: true })}
          className="rounded-xl border border-neutral-700 px-4 py-2 text-sm text-neutral-300 hover:bg-neutral-800"
        >
          Vai al login
        </button>
      </div>
    );
  }

  return (
    <>
      <SkipLink />
      <div className="flex h-[100svh] bg-[var(--bg-primary)] text-[var(--text-primary)] overflow-x-hidden max-w-full">
        {!recordingLock ? (
          <Navbar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
        ) : null}
        {/* FASE 4: Toggle sidebar collapse — visibile solo su desktop (md+) */}
        {!recordingLock && (
          <button
            onClick={toggleSidebar}
            className="hidden md:flex fixed left-0 top-1/2 -translate-y-1/2 z-[60] w-6 h-12 items-center justify-center rounded-r-xl bg-white/[0.04] border border-white/[0.10] border-l-0 hover:bg-white/[0.08] transition-all duration-300"
            style={{ left: sidebarCollapsed ? "64px" : "256px" }}
            aria-label={
              sidebarCollapsed ? "Espandi sidebar" : "Collassa sidebar"
            }
          >
            {sidebarCollapsed ? (
              <ChevronRight size={14} className="text-white/50" />
            ) : (
              <ChevronLeft size={14} className="text-white/50" />
            )}
          </button>
        )}
        {/* md:ml-64 compensa la sidebar fixed (w-64 = 256px) su desktop.
            pb-24 (96px) lascia spazio per la bottom bar mobile (~72px + safe area).
            overflow-x-hidden previene scroll orizzontale su mobile.
            Il margin-left è garantito anche dalle regole CSS #main-content in global.css */}
        <main
          id="main-content"
          tabIndex={-1}
          data-scroll-container="main"
          className={`flex-1 min-w-0 min-h-0 overflow-y-auto overflow-x-hidden p-4 md:pr-8 md:pl-6 relative safe-bottom transition-all duration-300 ${sidebarCollapsed ? "md:ml-16" : "md:ml-64"}`}
        >
          <ErrorBoundary resetKey={location.pathname}>
            <Suspense fallback={<LoadingFallback />}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={location.pathname}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                >
                  <Outlet />
                </motion.div>
              </AnimatePresence>
            </Suspense>
          </ErrorBoundary>
        </main>

        {/* Interactive Onboarding: tour feature al primo login */}
        <InteractiveOnboarding
          open={showOnboarding}
          onClose={() => {
            setShowOnboarding(false);
            localStorage.setItem("rc-onboarding-tour-done", "1");
          }}
        />

        {/* Feature Tour: mostrato dopo il tour onboarding */}
        <FeatureTour
          open={showTour}
          onClose={() => {
            setShowTour(false);
            localStorage.setItem("rc-tour-done", "1");
          }}
        />

        {/* GDPR: richiesta di accettazione dell'Informativa Privacy quando
            l'utente non ha ancora accettato la versione corrente. */}
        <PrivacyConsentGate />
      </div>
    </>
  );
};

export default Layout;
